import { useQuery } from '@apollo/client';
import { CalendarClock, Stethoscope, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { MEDICAL_QUEUE_BOARD_QUERY, WAITING_ROOM_BOARD_QUERY } from './display-boards.gql';

import { cn } from '@/shared/lib/cn';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';

interface MedicalQueueCard {
  sale_order_id: string;
  customer_name: string | null;
  pet_name: string | null;
  pet_age_label: string | null;
  current_weight_grams: number | null;
  cashier_initial_notes: string | null;
  primary_item_label: string | null;
  consulting_room_name: string | null;
  queued_at: string;
}

interface MedicalQueueResponse {
  medicalQueueBoard: MedicalQueueCard[];
}

interface WaitingRoomCard {
  consulting_room_id: string;
  consulting_room_name: string;
  occupancy_status: 'free' | 'occupied';
  veterinarian_name: string | null;
}

interface WaitingRoomResponse {
  waitingRoomBoard: WaitingRoomCard[];
}

function queueTimeLabel(queued_at: string): string {
  if (!queued_at) return '—';
  const date = new Date(queued_at);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function MedicalQueueBoardPage(): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<MedicalQueueResponse>(MEDICAL_QUEUE_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15_000,
  });
  const { data: rooms_data } = useQuery<WaitingRoomResponse>(WAITING_ROOM_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15_000,
  });
  const rows = data?.medicalQueueBoard ?? [];
  const rooms = rooms_data?.waitingRoomBoard ?? [];
  const free_rooms = rooms.filter((room) => room.occupancy_status === 'free').length;
  const busy_rooms = rooms.length - free_rooms;

  return (
    <div className="space-y-5">
      <header className="rounded-2xl border border-[#d6e4f1] bg-gradient-to-r from-white via-[#f8fbff] to-[#eef4ff] p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight text-[#121722]">
              {t('display.medical_queue.title')}
            </h2>
            <p className="text-sm text-slate-600">{t('display.medical_queue.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#eef2ff] px-3 py-1.5 text-xs font-semibold text-[#1b1463]">
              <Users className="h-3.5 w-3.5" aria-hidden />
              {t('display.medical_queue.waiting_count')}: {rows.length}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              {t('display.medical_queue.rooms_available')}: {free_rooms}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-700">
              {t('display.medical_queue.rooms_occupied')}: {busy_rooms}
            </span>
          </div>
        </div>
      </header>

      {rooms.length > 0 ? (
        <section className="space-y-2 rounded-2xl border border-[#d6e4f1] bg-white/95 p-3 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('display.medical_queue.zones_title')}
          </p>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {rooms.map((room) => {
              const is_free = room.occupancy_status === 'free';
              return (
                <Card
                  key={room.consulting_room_id}
                  className={cn(
                    'border bg-white shadow-sm',
                    is_free ? 'border-emerald-300/70' : 'border-indigo-300/70',
                  )}
                >
                  <CardContent className="flex items-center justify-between py-3">
                    <p className="text-sm font-semibold text-[#121722]">{room.consulting_room_name}</p>
                    <p
                      className={
                        is_free
                          ? 'text-sm font-semibold text-emerald-700'
                          : 'text-sm font-semibold text-indigo-700'
                      }
                    >
                      {is_free
                        ? t('display.medical_queue.free')
                        : room.veterinarian_name ?? t('display.medical_queue.assigned_doctor')}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      ) : null}

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            {t('display.medical_queue.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((row, index) => (
            <Card key={row.sale_order_id} className="border-[#d6e4f1] bg-white shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">
                    #{index + 1} - {row.pet_name ?? t('display.medical_queue.no_pet')}
                  </CardTitle>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#eef2ff] px-2 py-1 text-[11px] font-semibold text-[#1b1463]">
                    <Stethoscope className="h-3 w-3" aria-hidden />
                    {row.consulting_room_name ?? t('display.medical_queue.no_consulting_room')}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="font-medium text-[#121722]">
                  {row.customer_name ?? t('display.medical_queue.no_customer')}
                </p>
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  {t('display.medical_queue.queued_at')}: {queueTimeLabel(row.queued_at)}
                </p>
                {row.primary_item_label ? (
                  <p className="text-muted-foreground">{row.primary_item_label}</p>
                ) : null}
                {row.pet_age_label ? (
                  <p className="text-muted-foreground">
                    {t('display.medical_queue.age')}: {row.pet_age_label}
                  </p>
                ) : null}
                {row.current_weight_grams ? (
                  <p className="text-muted-foreground">
                    {t('display.medical_queue.weight')}:{' '}
                    {(row.current_weight_grams / 1000).toFixed(2)} kg
                  </p>
                ) : null}
                {row.cashier_initial_notes ? (
                  <p className="rounded-md border border-[#d6e4f1] bg-[#f8fbff] px-2 py-1 text-xs text-slate-700">
                    {row.cashier_initial_notes}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
